import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import { injectStateService } from '@/shared/services/StateService';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TaskQueryKey } from '@/shared/hooks/task/queryKey';
import { showWhenPermission } from '@/shared/components/security';
import AuthService, { functionalities, privileges } from '@/shared/services/AuthService';
import { useGetWeekPlanner } from '@/shared/hooks/task/useGetWeekPlanner';
import { DragDropContext, Draggable, Droppable, } from '@hello-pangea/dnd';
import classNames from 'classnames';
import moment from 'moment';
import WeekPlannerElementComponent from '@/modules/tasks/weekplanner/partial/WeekPlannerElement';
import { CalendarEventShowAs, } from '@/modules/tasks/types';
import { TaskStatus } from '@/modules/tasks/taskboard/kanban/constants';
import { useGetWeekPlannerCapacity } from '@/shared/hooks/task/useGetWeekPlannerCapacity';
import { useGetWeekPlannerPlannedMinutes, weekPlannerPlannedMinutesQueryKey, } from '@/shared/hooks/task/useGetWeekPlannerPlannedMinutes';
import { useFitToViewportBottom } from '@/shared/hooks/useFitToViewportBottom';
import { useGetWeekPlannerExternalEvents } from '@/shared/hooks/task/useGetWeekPlannerExternalEvents';
import DataCacheService from '@/shared/services/DataCacheService';
import { ConnectPlatform } from '@/shared/connectPlatformConstants';
import { minutesToTimeSpan } from '@/shared/utils/adminpulse';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import TaskDetailsModal from '@/modules/tasks/taskDetails/TaskDetailsModal';
import CreateToDoModal from '@/modules/tasks/CreateToDoModal';
import { useIntl } from 'react-intl';
import dateFormatService from '@/shared/services/DateFormatService';
import { WeekPlannerUnplannedTasksPanel } from '@/modules/tasks/weekplanner/partial/WeekPlannerUnplannedTasksPanel';
import { useGetWeekPlannerUnplannedTasks, } from '@/shared/hooks/task/useGetWeekPlannerUnplannedTasks';
import { createDefaultUnplannedTasksFilters, UNPLANNED_DRAG_PREFIX, UNPLANNED_TASKS_DROPPABLE_ID, } from '@/modules/tasks/weekplanner/unplannedTasksConstants';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import YesNoModal from '@/shared/components/YesNoModal';
import Checkbox from '@/shared/components/input/Checkbox';
import ToastrService from '@/shared/services/ToastrService';
import { useBulkUpdateExecutionDate, } from '@/shared/hooks/task/useBulkUpdateExecutionDate';
import { useWeekPlannerPlanProposal } from '@/shared/hooks/task/useWeekPlannerPlanProposal';
import taskMessages from '@/modules/tasks/taskMessages';
import SignalRService from '@/shared/services/SignalRService';
import ChangeAssignedUserModal from '@/modules/tasks/taskList/ChangeAssignedUserModal';
import weekPlannerMessages from './weekPlannerMessages';
import { WeekPlannerHeader } from './partial/WeekPlannerHeader';
import { WeekPlannerSelectionActions } from './partial/WeekPlannerSelectionActions';
import { WeekPlannerExecutionDateModal } from './partial/WeekPlannerExecutionDateModal';
import { WeekPlannerPlanProposalModal } from './partial/WeekPlannerPlanProposalModal';
const StableDragDropContext = DragDropContext;
const StorageKey = 'WeekPlannerParams';
const UnplannedTasksStorageKey = 'WeekPlannerUnplannedTasksParams';
const getUnplannedTasksStateFromStorage = () => {
    var _a;
    const defaultFilters = createDefaultUnplannedTasksFilters();
    const stored = sessionStorage.getItem(UnplannedTasksStorageKey);
    if (!stored)
        return { isOpen: false, showFilters: false, filters: defaultFilters };
    try {
        const parsed = JSON.parse(stored);
        return {
            isOpen: !!parsed.isOpen,
            showFilters: !!parsed.showFilters,
            filters: Object.assign(Object.assign({}, defaultFilters), ((_a = parsed.filters) !== null && _a !== void 0 ? _a : {})),
        };
    }
    catch (_b) {
        return { isOpen: false, showFilters: false, filters: defaultFilters };
    }
};
const getEmptyState = () => {
    const executionDateFrom = moment
        .utc()
        .startOf('day')
        .startOf('isoWeek');
    return {
        taskTemplates: [],
        userId: AuthService.getUserId(),
        status: null,
        executionDateFrom: executionDateFrom.format('YYYY-MM-DD'),
        executionDateUntil: executionDateFrom
            .clone()
            .add(6, 'days')
            .format('YYYY-MM-DD'),
        showWeekend: false,
    };
};
const getFiltersFromStorage = props => {
    const emptyState = getEmptyState();
    if (!!props.filters && props.filters.executionDateFrom && props.filters.userId) {
        const filterFromRoute = Object.assign(Object.assign({}, emptyState), { userId: props.filters.userId, executionDateFrom: props.filters.executionDateFrom, executionDateUntil: moment(props.filters.executionDateFrom)
                .add(6, 'days')
                .format('YYYY-MM-DD') });
        sessionStorage.setItem(StorageKey, JSON.stringify(filterFromRoute));
        return filterFromRoute;
    }
    const storageString = sessionStorage.getItem(StorageKey);
    if (!storageString) {
        return emptyState;
    }
    try {
        const filterObject = JSON.parse(storageString);
        return Object.assign(Object.assign({}, emptyState), filterObject);
    }
    catch (error) {
        return emptyState;
    }
};
const getResourceTypeLabel = (intl, type) => {
    const key = `resourcetype_${type}`;
    const translated = intl.formatMessage({ id: key, defaultMessage: key });
    return translated !== key ? translated : key;
};
const endsAtUtcMidnight = (isoEnd) => {
    const end = moment.utc(isoEnd);
    return end.hours() === 0 && end.minutes() === 0 && end.seconds() === 0;
};
// Indexed by isoWeekday() - 1 (Monday = 1 .. Sunday = 7).
const weekdayMessages = [
    weekPlannerMessages.Monday,
    weekPlannerMessages.Tuesday,
    weekPlannerMessages.Wednesday,
    weekPlannerMessages.Thursday,
    weekPlannerMessages.Friday,
    weekPlannerMessages.Saturday,
    weekPlannerMessages.Sunday,
];
// Done tasks are excluded from selection: bulk actions only make sense for unfinished work.
const isSelectable = (task) => task.status !== TaskStatus.Done;
const sortColumnItems = (items, locale) => {
    const groupName = (t) => { var _a, _b, _c; return (_c = (t.parentTaskId ? (_a = t.parentName) !== null && _a !== void 0 ? _a : t.parentTemplateName : (_b = t.name) !== null && _b !== void 0 ? _b : t.templateName)) !== null && _c !== void 0 ? _c : ''; };
    return [...items].sort((a, b) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const byRelation = ((_b = (_a = a.relation) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : '').localeCompare((_d = (_c = b.relation) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : '', locale, {
            sensitivity: 'base',
        });
        if (byRelation !== 0)
            return byRelation;
        const byGroupName = groupName(a).localeCompare(groupName(b), locale, { sensitivity: 'base' });
        if (byGroupName !== 0)
            return byGroupName;
        const groupKeyA = (_e = a.parentTaskId) !== null && _e !== void 0 ? _e : a.id;
        const groupKeyB = (_f = b.parentTaskId) !== null && _f !== void 0 ? _f : b.id;
        if (groupKeyA !== groupKeyB)
            return groupKeyA < groupKeyB ? -1 : 1;
        const byLevel = (a.parentTaskId ? 1 : 0) - (b.parentTaskId ? 1 : 0);
        if (byLevel !== 0)
            return byLevel;
        return ((_g = a.priority) !== null && _g !== void 0 ? _g : Number.MAX_SAFE_INTEGER) - ((_h = b.priority) !== null && _h !== void 0 ? _h : Number.MAX_SAFE_INTEGER);
    });
};
const externalEventOverlapsDay = (event, day) => {
    if (event.isAllDay || endsAtUtcMidnight(event.end)) {
        const dayStr = day.format('YYYY-MM-DD');
        return event.start.slice(0, 10) <= dayStr && dayStr < event.end.slice(0, 10);
    }
    const nextDay = day.clone().add(1, 'day');
    return moment(event.start).isBefore(nextDay) && moment(event.end).isAfter(day);
};
const WeekPlannerContainer = props => {
    var _a, _b;
    const queryClient = useQueryClient();
    const intl = useIntl();
    const [columns, setColumns] = useState([]);
    const canUpdateTask = useMemo(() => AuthService.hasPermission({
        functionality: functionalities.PlannedTask,
        privilege: privileges.Update,
    }), []);
    const { stateService } = props;
    const [activeFilters, setActiveFilters] = useState((_a = getFiltersFromStorage(props)) !== null && _a !== void 0 ? _a : {});
    useEffect(() => {
        sessionStorage.setItem(StorageKey, JSON.stringify(activeFilters));
    }, [activeFilters]);
    const showWeekend = !!activeFilters.showWeekend;
    const setShowWeekend = (value) => setActiveFilters(prev => (Object.assign(Object.assign({}, prev), { showWeekend: value })));
    const { data, isLoading, refetch: onRefetch } = useGetWeekPlanner(activeFilters);
    const setWeekPlannerLayoutRef = useFitToViewportBottom(!(isLoading && !data), 'height');
    const { data: capacityData } = useGetWeekPlannerCapacity({
        userId: activeFilters.userId,
        from: activeFilters.executionDateFrom,
        to: activeFilters.executionDateUntil,
    });
    const { data: plannedMinutesData } = useGetWeekPlannerPlannedMinutes({
        userId: activeFilters.userId,
        executionDateFrom: activeFilters.executionDateFrom,
        executionDateUntil: activeFilters.executionDateUntil,
    });
    const plannedMinutesPerDay = useMemo(() => {
        const map = new Map();
        (plannedMinutesData || []).forEach(day => map.set(moment(day.date).format('YYYY-MM-DD'), day.minutes));
        return map;
    }, [plannedMinutesData]);
    const isViewingSelf = activeFilters.userId === AuthService.getUserId();
    const hasOutlookCalendarConnection = useMemo(() => DataCacheService.hasUserConnectPlatform(ConnectPlatform.OutlookCalendar), []);
    const externalEventsEnabled = AuthService.hasFeature('CapacityPlanning') && isViewingSelf && hasOutlookCalendarConnection;
    const { data: externalEventsData } = useGetWeekPlannerExternalEvents({ from: activeFilters.executionDateFrom, to: activeFilters.executionDateUntil }, externalEventsEnabled);
    const externalEvents = externalEventsEnabled ? externalEventsData : undefined;
    const buildColumn = useCallback((items, index, date, dayCapacity, dayExternalEvents = []) => {
        var _a, _b, _c;
        // Every column-construction path (initial build, drags, bulk moves, merges) runs through
        // here, so sorting once in this spot keeps the order correct even during optimistic updates.
        const sortedItems = sortColumnItems(items, intl.locale);
        const totalTime = (_a = plannedMinutesPerDay.get(date.format('YYYY-MM-DD'))) !== null && _a !== void 0 ? _a : 0;
        const registrations = (dayCapacity === null || dayCapacity === void 0 ? void 0 : dayCapacity.registrations) || [];
        const expectedWorkingMinutes = (_b = dayCapacity === null || dayCapacity === void 0 ? void 0 : dayCapacity.expectedWorkingMinutes) !== null && _b !== void 0 ? _b : 0;
        const isHoliday = (_c = dayCapacity === null || dayCapacity === void 0 ? void 0 : dayCapacity.isHoliday) !== null && _c !== void 0 ? _c : false;
        const registrationMinutes = registrations.reduce((sum, r) => sum + r.durationMinutes, 0);
        const workingMinutes = expectedWorkingMinutes - registrationMinutes;
        const availableMinutes = workingMinutes - totalTime;
        return {
            index,
            date,
            totalTime,
            items: sortedItems,
            registrations,
            externalEvents: dayExternalEvents,
            workingMinutes,
            isHoliday,
            availableMinutes,
            id: date.format('YYYY-MM-DD'),
        };
    }, [intl.locale, plannedMinutesPerDay]);
    useEffect(() => {
        const content = [];
        const base = moment(activeFilters.executionDateFrom).startOf('isoWeek');
        const dayCount = showWeekend ? 7 : 5;
        for (let i = 0; i < dayCount; i++) {
            const day = base.clone().add(i, 'days');
            const nextDay = base.clone().add(i + 1, 'days');
            const items = (data || []).filter(x => {
                var _a;
                const positioningDate = (_a = x.executionDate) !== null && _a !== void 0 ? _a : x.deadline;
                return moment(positioningDate).isBetween(day, nextDay, 'days', '[)');
            });
            const dayStr = day.format('YYYY-MM-DD');
            const dayCapacity = (capacityData || []).find(c => moment(c.date).format('YYYY-MM-DD') === dayStr);
            const dayExternalEvents = (externalEvents || []).filter(e => externalEventOverlapsDay(e, day));
            content.push(buildColumn(items, i, day, dayCapacity, dayExternalEvents));
        }
        setColumns(content);
    }, [
        data,
        capacityData,
        externalEvents,
        activeFilters.executionDateFrom,
        showWeekend,
        buildColumn,
    ]);
    const weekendTaskCount = useMemo(() => (data || []).filter(x => {
        var _a;
        const positioningDate = (_a = x.executionDate) !== null && _a !== void 0 ? _a : x.deadline;
        const isoDay = moment(positioningDate).isoWeekday();
        return isoDay === 6 || isoDay === 7;
    }).length, [data]);
    // Selected task ids mapped to their taskLevel (the bulk endpoint needs both).
    const [selectedTasks, setSelectedTasks] = useState(new Map());
    const [multiDragActive, setMultiDragActive] = useState(false);
    const [showSetDateModal, setShowSetDateModal] = useState(false);
    const [showProposalModal, setShowProposalModal] = useState(false);
    const [showChangeAssignedUserModal, setShowChangeAssignedUserModal] = useState(false);
    const pendingAssignedUserJobRef = useRef(null);
    useEffect(() => {
        const onJobUpdated = (job) => {
            if (!pendingAssignedUserJobRef.current || job.id !== pendingAssignedUserJobRef.current)
                return;
            if (job.status === 0 || job.status === 1)
                return;
            pendingAssignedUserJobRef.current = null;
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlannerUnplannedTasks] });
        };
        SignalRService.subscribe('jobUpdated', onJobUpdated);
        return () => SignalRService.unsubscribe('jobUpdated', onJobUpdated);
    }, [queryClient]);
    const { bulkUpdateExecutionDate, isLoading: isBulkUpdating } = useBulkUpdateExecutionDate();
    const { getPlanProposal, isLoading: isProposalLoading, data: proposalRows, } = useWeekPlannerPlanProposal();
    // Anchor for shift+click range selection: the last individually clicked card and the list
    // (unplanned panel or one day column) it belongs to. Ranges never span lists.
    const lastSelectedRef = useRef(null);
    const clearSelection = () => {
        lastSelectedRef.current = null;
        setSelectedTasks(new Map());
    };
    const taskTemplatesKey = (activeFilters.taskTemplates || []).join(',');
    // Clear when the visible set can change — bulk actions must never hit off-screen cards. Depends
    // on filter values (not object identity): filter inputs may re-emit unchanged filters.
    useEffect(() => {
        lastSelectedRef.current = null;
        setSelectedTasks(new Map());
    }, [
        activeFilters.executionDateFrom,
        activeFilters.executionDateUntil,
        activeFilters.userId,
        activeFilters.status,
        taskTemplatesKey,
        activeFilters.showWeekend,
    ]);
    // Tasks completed while selected must drop out — done tasks are never selectable.
    useEffect(() => {
        if (!data)
            return;
        setSelectedTasks(prev => {
            let changed = false;
            const next = new Map(prev);
            for (const task of data) {
                if (task.status === TaskStatus.Done && next.has(task.id)) {
                    next.delete(task.id);
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [data]);
    const pruneDoneFromSelection = (updatedTasks) => {
        const doneIds = (updatedTasks || []).filter(t => t.status === TaskStatus.Done).map(t => t.id);
        if (doneIds.length === 0)
            return;
        setSelectedTasks(prev => {
            const next = new Map(prev);
            doneIds.forEach(id => next.delete(id));
            return next;
        });
    };
    const toggleTaskSelected = (element) => {
        if (!isSelectable(element))
            return;
        setSelectedTasks(prev => {
            var _a;
            const next = new Map(prev);
            if (next.has(element.id))
                next.delete(element.id);
            else
                next.set(element.id, (_a = element.taskLevel) !== null && _a !== void 0 ? _a : 0);
            return next;
        });
    };
    const setManySelected = (items, selected) => setSelectedTasks(prev => {
        var _a;
        const next = new Map(prev);
        for (const item of items) {
            if (selected) {
                if (isSelectable(item))
                    next.set(item.id, (_a = item.taskLevel) !== null && _a !== void 0 ? _a : 0);
            }
            else {
                next.delete(item.id);
            }
        }
        return next;
    });
    // Per-card selection click. Shift+click applies the clicked card's new state to every card
    // between the anchor and it (within the same list); anything else is a plain toggle.
    const onSelectionClick = (element, event, listId, listItems) => {
        var _a;
        if (!isSelectable(element))
            return;
        const anchor = lastSelectedRef.current;
        const selecting = !selectedTasks.has(element.id);
        // Only a click that leaves the card selected becomes the anchor; unselecting clears it, so a
        // shift+click after an unselect acts as a plain select instead of ranging from a dead anchor.
        lastSelectedRef.current = selecting ? { listId, taskId: element.id } : null;
        if ((event === null || event === void 0 ? void 0 : event.shiftKey) && anchor && anchor.listId === listId && selectedTasks.has(anchor.taskId)) {
            const anchorIndex = listItems.findIndex(x => x.id === anchor.taskId);
            const clickedIndex = listItems.findIndex(x => x.id === element.id);
            if (anchorIndex !== -1 && clickedIndex !== -1) {
                (_a = window.getSelection()) === null || _a === void 0 ? void 0 : _a.removeAllRanges();
                const range = listItems.slice(Math.min(anchorIndex, clickedIndex), Math.max(anchorIndex, clickedIndex) + 1);
                setManySelected(range, selecting);
                return;
            }
        }
        toggleTaskSelected(element);
    };
    const visibleTasks = useMemo(() => columns.flatMap(c => c.items), [columns]);
    const selectableTasks = useMemo(() => visibleTasks.filter(isSelectable), [visibleTasks]);
    const allSelected = selectableTasks.length > 0 && selectableTasks.every(t => selectedTasks.has(t.id));
    const toggleSelectAll = (selected) => setManySelected(selectableTasks, selected);
    const selectionItems = (executionDate) => Array.from(selectedTasks.entries()).map(([id, taskLevel]) => ({
        taskLevel,
        id,
        executionDate,
    }));
    const [taskDetailModal, setTaskDetailModal] = useState({ task: undefined, open: false });
    const [taskToDelete, setTaskToDelete] = useState(null);
    const deleteTodo = (todo) => {
        api.base
            .delete(`${config.appUrls.tasks}/api/PlannedTasks/${todo.id}`, { params: { taskLevel: 2 } })
            .then(() => {
            setTaskToDelete(null);
            setColumns(prev => prev.map(c => buildColumn(c.items.filter(i => i.id !== todo.id), c.index, c.date, findDayCapacity(c.date), findDayExternalEvents(c.date))));
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
        }, error => {
            setTaskToDelete(null);
            handleHttpError(intl)(error);
        });
    };
    const openTaskDetailModal = task => setTaskDetailModal({ task, open: true });
    const [showCreateToDoModal, setShowCreateToDoModal] = useState(false);
    const initialUnplannedTasks = useMemo(getUnplannedTasksStateFromStorage, []);
    const [isUnplannedTasksOpen, setUnplannedTasksOpen] = useState(initialUnplannedTasks.isOpen);
    const [showUnplannedTasksFilters, setShowUnplannedTasksFilters] = useState(initialUnplannedTasks.showFilters);
    const [unplannedTasksFilters, setUnplannedTasksFilters] = useState(initialUnplannedTasks.filters);
    useEffect(() => {
        sessionStorage.setItem(UnplannedTasksStorageKey, JSON.stringify({
            isOpen: isUnplannedTasksOpen,
            showFilters: showUnplannedTasksFilters,
            filters: unplannedTasksFilters,
        }));
    }, [isUnplannedTasksOpen, showUnplannedTasksFilters, unplannedTasksFilters]);
    const effectiveUnplannedTasksFilters = useMemo(() => (Object.assign(Object.assign({}, unplannedTasksFilters), { userId: activeFilters.userId })), [unplannedTasksFilters, activeFilters.userId]);
    const { items: unplannedTaskItems, isLoading: isUnplannedTasksLoading, isFetchingNextPage: isUnplannedTasksFetchingNextPage, fetchNextPage: fetchNextUnplannedTasksPage, hasNextPage: hasNextUnplannedTasksPage, } = useGetWeekPlannerUnplannedTasks(effectiveUnplannedTasksFilters, isUnplannedTasksOpen);
    // Deadlines of the current selection, so the bulk execution-date modal can warn about tasks that
    // would be planned past their deadline. A selected task lives in the grid, the panel, or both.
    const selectedTaskDeadlines = useMemo(() => {
        const byId = new Map();
        unplannedTaskItems.forEach(task => byId.set(task.id, task));
        visibleTasks.forEach(task => byId.set(task.id, task));
        return Array.from(selectedTasks.keys()).map(id => { var _a, _b; return (_b = (_a = byId.get(id)) === null || _a === void 0 ? void 0 : _a.deadline) !== null && _b !== void 0 ? _b : null; });
    }, [selectedTasks, visibleTasks, unplannedTaskItems]);
    // Panel filter changes also change which cards are visible — same off-screen-selection rule.
    const unplannedTasksFiltersKey = JSON.stringify(unplannedTasksFilters);
    useEffect(() => {
        lastSelectedRef.current = null;
        setSelectedTasks(new Map());
    }, [unplannedTasksFiltersKey]);
    const findDayCapacity = (date) => {
        const dayStr = date.format('YYYY-MM-DD');
        return (capacityData || []).find(c => moment(c.date).format('YYYY-MM-DD') === dayStr);
    };
    const findDayExternalEvents = (date) => (externalEvents || []).filter(e => externalEventOverlapsDay(e, date));
    const formatExternalEventWhen = (event) => {
        if (event.isAllDay) {
            // Date-only handling — converting the exclusive midnight-UTC end to local time would land
            // it on the wrong calendar day.
            const startDate = moment(event.start.slice(0, 10));
            const inclusiveEnd = moment(event.end.slice(0, 10)).subtract(1, 'day');
            return startDate.isSame(inclusiveEnd, 'day')
                ? intl.formatMessage(weekPlannerMessages.AllDay)
                : `${startDate.format('DD/MM')} – ${inclusiveEnd.format('DD/MM')}`;
        }
        const start = moment(event.start);
        const end = moment(event.end);
        const endDay = end.clone().subtract(1, 'second');
        return start.isSame(endDay, 'day')
            ? `${start.format('HH:mm')}–${end.format('HH:mm')}`
            : `${start.format('DD/MM HH:mm')} – ${end.format('DD/MM HH:mm')}`;
    };
    const closeTaskDetailModal = tasks => {
        setTaskDetailModal({ task: undefined, open: false });
        if (!tasks || tasks.length === 0)
            return;
        pruneDoneFromSelection(tasks);
        const map = {};
        for (const task of tasks)
            map[task.id] = task;
        const newColumns = columns.map(c => buildColumn(c.items.map(i => (i.id in map ? Object.assign(Object.assign({}, i), map[i.id]) : i)), c.index, c.date, findDayCapacity(c.date), findDayExternalEvents(c.date)));
        setColumns(newColumns);
        queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
        queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlannerUnplannedTasks] });
    };
    const refetch = () => {
        if (isLoading) {
            const queryKey = [TaskQueryKey.WeekPlanner];
            queryClient.cancelQueries(queryKey);
            queryClient.invalidateQueries({ queryKey });
        }
        return onRefetch();
    };
    const setFilter = (filter) => {
        setActiveFilters(prev => {
            const newFilter = Object.assign(Object.assign({}, prev), filter);
            sessionStorage.setItem(StorageKey, JSON.stringify(newFilter));
            return newFilter;
        });
        setTimeout(() => {
            refetch();
        }, 0);
    };
    const removeFromUnplannedTasksCache = (taskId) => queryClient.setQueryData([TaskQueryKey.WeekPlannerUnplannedTasks, effectiveUnplannedTasksFilters], prev => {
        if (!prev)
            return prev;
        return Object.assign(Object.assign({}, prev), { pages: prev.pages.map(page => page.filter(t => t.id !== taskId)) });
    });
    const updateExecutionInformation = (taskId, newExecutionDate) => {
        const fromColumn = columns.find(c => c.items.some(i => i.id === taskId));
        if (!fromColumn)
            return;
        const task = fromColumn.items.find(t => t.id === taskId);
        if (!task)
            return;
        const toColumn = columns.find(c => c.id === newExecutionDate);
        const sameColumn = fromColumn === toColumn;
        // A move to the same column is only a no-op when the task already sits there via its execution
        // date. A deadline-positioned task (no execution date yet) dropped on its own deadline day is a
        // real change (null -> that date), so let it through to fire the PUT and leave the panel.
        if (sameColumn && task.executionDate)
            return;
        // Copy instead of mutating: the cached task object is shared across column rebuilds and
        // cache merges, so in-place changes would leak optimistic state into later merges.
        const movedTask = Object.assign(Object.assign({}, task), { executionDate: moment(newExecutionDate) });
        if (sameColumn) {
            setColumns(prev => prev.map(c => buildColumn(c.items.map(i => (i.id === taskId ? movedTask : i)), c.index, c.date, findDayCapacity(c.date), findDayExternalEvents(c.date))));
        }
        else {
            const newColumns = [...columns];
            const fromItems = fromColumn.items.filter(i => i.id !== taskId);
            newColumns[fromColumn.index] = buildColumn(fromItems, fromColumn.index, fromColumn.date, findDayCapacity(fromColumn.date), findDayExternalEvents(fromColumn.date));
            if (toColumn) {
                const toItems = [...toColumn.items, movedTask];
                newColumns[toColumn.index] = buildColumn(toItems, toColumn.index, toColumn.date, findDayCapacity(toColumn.date), findDayExternalEvents(toColumn.date));
            }
            setColumns(newColumns);
        }
        return api.base
            .put(`${config.appUrls.tasks}/api/weekPlanner/execution/${task.taskLevel}/${taskId}`, {
            executionDate: newExecutionDate,
        })
            .then(({ data: updatedTask }) => {
            queryClient.setQueryData([TaskQueryKey.WeekPlanner, activeFilters], prev => (prev || []).map(t => (t.id === taskId ? Object.assign(Object.assign({}, t), updatedTask) : t)));
            // The task now has an execution date, so drop it from the unplanned-tasks panel — patch
            removeFromUnplannedTasksCache(taskId);
            // This path patches the task cache instead of invalidating it, so the day totals (their
            // own query) have to be refreshed explicitly.
            queryClient.invalidateQueries({ queryKey: weekPlannerPlannedMinutesQueryKey });
        }, error => {
            handleHttpError(intl)(error);
        });
    };
    const mergeUpdatedTasks = (updatedTasks) => {
        if (!updatedTasks || updatedTasks.length === 0)
            return;
        pruneDoneFromSelection(updatedTasks);
        const map = {};
        for (const t of updatedTasks)
            map[t.id] = t;
        setColumns(prev => prev.map(c => buildColumn(c.items.map(i => (i.id in map ? Object.assign(Object.assign({}, i), map[i.id]) : i)), c.index, c.date, findDayCapacity(c.date), findDayExternalEvents(c.date))));
        queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
        queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlannerUnplannedTasks] });
    };
    const onToDoCreated = todo => {
        var _a;
        setShowCreateToDoModal(false);
        if (!todo)
            return;
        // The grid positions a to-do by its execution date, falling back to the deadline.
        const positioningDate = moment((_a = todo.executionDate) !== null && _a !== void 0 ? _a : todo.deadline);
        const weekStart = moment(activeFilters.executionDateFrom);
        const weekEnd = moment(activeFilters.executionDateUntil);
        if (positioningDate.isBetween(weekStart, weekEnd, 'day', '[]')) {
            refetch();
        }
        else if (!todo.executionDate) {
            // Unplanned: it belongs in the side panel rather than on the board.
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlannerUnplannedTasks] });
        }
    };
    const moveFromUnplannedTasksToDay = (taskId, targetDate) => {
        const task = unplannedTaskItems.find(b => b.id === taskId);
        if (!task)
            return undefined;
        removeFromUnplannedTasksCache(taskId);
        const targetColumn = columns.find(c => c.id === targetDate);
        if (targetColumn) {
            const taskWithExecution = Object.assign(Object.assign({}, task), { executionDate: targetDate });
            // Strip any existing copy of this task from all columns first — the task may already be
            // shown in the grid on its deadline, and two Draggables with the same id would break dnd.
            const newColumns = columns.map(c => buildColumn(c.items.filter(i => i.id !== taskId), c.index, c.date, findDayCapacity(c.date), findDayExternalEvents(c.date)));
            newColumns[targetColumn.index] = buildColumn([...newColumns[targetColumn.index].items, taskWithExecution], targetColumn.index, targetColumn.date, findDayCapacity(targetColumn.date), findDayExternalEvents(targetColumn.date));
            setColumns(newColumns);
        }
        return api.base
            .put(`${config.appUrls.tasks}/api/weekPlanner/execution/${task.taskLevel}/${taskId}`, {
            executionDate: targetDate,
        })
            .then(() => {
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
        }, error => {
            handleHttpError(intl)(error);
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlannerUnplannedTasks] });
        });
    };
    const onUpdateExecutionDate = (element, newDate) => {
        if (!newDate)
            return;
        const formatted = moment(newDate).format('YYYY-MM-DD');
        const isInColumn = columns.some(c => c.items.some(i => i.id === element.id));
        if (!isInColumn) {
            return moveFromUnplannedTasksToDay(element.id, formatted);
        }
        return updateExecutionInformation(element.id, formatted);
    };
    const moveFromDayToUnplannedTasks = (taskId, sourceColumnId) => {
        const fromColumn = columns.find(c => c.id === sourceColumnId);
        if (!fromColumn)
            return undefined;
        const task = fromColumn.items.find(i => i.id === taskId);
        if (!task)
            return undefined;
        const newColumns = [...columns];
        newColumns[fromColumn.index] = buildColumn(fromColumn.items.filter(i => i.id !== taskId), fromColumn.index, fromColumn.date, findDayCapacity(fromColumn.date), findDayExternalEvents(fromColumn.date));
        setColumns(newColumns);
        return api.base
            .put(`${config.appUrls.tasks}/api/weekPlanner/execution/${task.taskLevel}/${taskId}`, {
            executionDate: null,
        })
            .then(() => {
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlannerUnplannedTasks] });
        }, error => {
            handleHttpError(intl)(error);
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
            queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlannerUnplannedTasks] });
        });
    };
    const submitBulkAssignedUser = assignedToModel => {
        const model = {
            filterParams: {},
            selectAll: false,
            selectedTasks: Array.from(selectedTasks.keys()),
            parameters: assignedToModel,
        };
        return api.base
            .post(`${config.appUrls.tasks}/api/TaskList/BatchChangeAssignedUser`, model)
            .then(({ data: job }) => {
            var _a;
            setShowChangeAssignedUserModal(false);
            pendingAssignedUserJobRef.current = (_a = job === null || job === void 0 ? void 0 : job.id) !== null && _a !== void 0 ? _a : null;
            ToastrService.info(intl.formatMessage(taskMessages.UpdateAssignedUserQueued), undefined, job ? () => stateService.gotoJob(job.id) : undefined);
            clearSelection();
        })
            .catch(error => {
            handleHttpError(intl)(error);
        });
    };
    const submitBulkExecutionDate = (executionDate) => {
        const items = selectionItems(executionDate);
        if (items.length === 0)
            return;
        bulkUpdateExecutionDate(items).then(() => {
            ToastrService.success(intl.formatMessage(weekPlannerMessages.TasksUpdated, { count: items.length }));
            clearSelection();
            setShowSetDateModal(false);
        }, 
        // Errors are toasted by the hook; its invalidation restores the server state.
        () => undefined);
    };
    const moveSelectionToNextWeek = () => {
        var _a;
        // Unplanned (sidepanel-only) tasks have no day to move from — they land on next week's Monday.
        const nextWeekMonday = moment(activeFilters.executionDateFrom)
            .startOf('isoWeek')
            .add(7, 'days')
            .format('YYYY-MM-DD');
        const gridTasksById = new Map(visibleTasks.map(t => [t.id, t]));
        const items = [];
        for (const [id, taskLevel] of Array.from(selectedTasks.entries())) {
            const gridTask = gridTasksById.get(id);
            const positioningDate = gridTask ? (_a = gridTask.executionDate) !== null && _a !== void 0 ? _a : gridTask.deadline : null;
            items.push({
                taskLevel,
                id,
                executionDate: positioningDate
                    ? moment(positioningDate)
                        .add(7, 'days')
                        .format('YYYY-MM-DD')
                    : nextWeekMonday,
            });
        }
        if (items.length === 0)
            return;
        bulkUpdateExecutionDate(items).then(() => {
            ToastrService.success(intl.formatMessage(weekPlannerMessages.TasksMoved, { count: items.length }));
            clearSelection();
        }, () => undefined);
    };
    const openPlanProposal = () => {
        setShowProposalModal(true);
        getPlanProposal({
            tasks: Array.from(selectedTasks.entries()).map(([id, taskLevel]) => ({ taskLevel, id })),
            userId: activeFilters.userId,
        }).then(undefined, () => undefined);
    };
    const savePlanProposal = (accepted, skippedCount) => {
        if (accepted.length === 0) {
            setShowProposalModal(false);
            return;
        }
        bulkUpdateExecutionDate(accepted).then(() => {
            ToastrService.success(intl.formatMessage(weekPlannerMessages.ProposalApplied, {
                accepted: accepted.length,
                rejected: skippedCount,
            }));
            clearSelection();
            setShowProposalModal(false);
        }, () => undefined);
    };
    const applyBulkDragMove = (targetDate) => {
        const targetColumn = columns.find(c => c.id === targetDate);
        if (!targetColumn)
            return;
        // A task can sit in the grid and the panel at once (deadline-positioned) — the grid copy wins.
        const movedById = new Map();
        visibleTasks.forEach(t => {
            if (selectedTasks.has(t.id))
                movedById.set(t.id, t);
        });
        unplannedTaskItems.forEach(t => {
            if (selectedTasks.has(t.id) && !movedById.has(t.id))
                movedById.set(t.id, t);
        });
        const movedTasks = Array.from(movedById.values()).map(t => (Object.assign(Object.assign({}, t), { executionDate: targetDate })));
        const newColumns = columns.map(c => buildColumn(c.items.filter(i => !selectedTasks.has(i.id)), c.index, c.date, findDayCapacity(c.date), findDayExternalEvents(c.date)));
        newColumns[targetColumn.index] = buildColumn([...newColumns[targetColumn.index].items, ...movedTasks], targetColumn.index, targetColumn.date, findDayCapacity(targetColumn.date), findDayExternalEvents(targetColumn.date));
        setColumns(newColumns);
        Array.from(selectedTasks.keys()).forEach(removeFromUnplannedTasksCache);
        bulkUpdateExecutionDate(selectionItems(targetDate)).then(() => clearSelection(), () => undefined);
    };
    const applyBulkDragUnplan = () => {
        const items = selectionItems(null);
        if (items.length === 0)
            return;
        const removedIds = new Set(items.map(i => i.id));
        const newColumns = columns.map(c => buildColumn(c.items.filter(i => !removedIds.has(i.id)), c.index, c.date, findDayCapacity(c.date), findDayExternalEvents(c.date)));
        setColumns(newColumns);
        bulkUpdateExecutionDate(items).then(() => clearSelection(), () => undefined);
    };
    const DRAGGING_BODY_CLASS = 'week-planner-dragging';
    const onDragStart = (start) => {
        document.body.classList.add(DRAGGING_BODY_CLASS);
        // Panel draggable ids are prefixed — strip it so panel cards join the selection check too.
        const draggedTaskId = start.draggableId.replace(UNPLANNED_DRAG_PREFIX, '');
        setMultiDragActive(selectedTasks.has(draggedTaskId));
    };
    const onDragEnd = (result) => {
        document.body.classList.remove(DRAGGING_BODY_CLASS);
        setMultiDragActive(false);
        if (!canUpdateTask)
            return;
        if (!result.destination)
            return;
        const target = result.destination.droppableId;
        const source = result.source.droppableId;
        const item = result.draggableId;
        const isMultiDrag = selectedTasks.has(item.replace(UNPLANNED_DRAG_PREFIX, ''));
        if (isMultiDrag) {
            if (target === UNPLANNED_TASKS_DROPPABLE_ID) {
                return applyBulkDragUnplan();
            }
            return applyBulkDragMove(target);
        }
        if (target === UNPLANNED_TASKS_DROPPABLE_ID) {
            if (source === UNPLANNED_TASKS_DROPPABLE_ID)
                return;
            return moveFromDayToUnplannedTasks(item, source);
        }
        if (source === UNPLANNED_TASKS_DROPPABLE_ID) {
            // Panel draggable ids are prefixed to stay unique vs the grid copy — recover the real id.
            const taskId = item.replace(UNPLANNED_DRAG_PREFIX, '');
            return moveFromUnplannedTasksToDay(taskId, target);
        }
        return updateExecutionInformation(item, target);
    };
    const renderItem = (provided, snapshot, item, column) => {
        const isSelected = selectedTasks.has(item.id);
        return (_jsxs("div", Object.assign({ ref: provided.innerRef }, provided.draggableProps, provided.dragHandleProps, { className: classNames('week-planner-box', {
                'week-planner-box-dimmed': multiDragActive && isSelected && !snapshot.isDragging,
            }) }, { children: [snapshot.isDragging && multiDragActive && selectedTasks.size > 1 && (_jsx("span", Object.assign({ className: "week-planner-multi-drag-badge" }, { children: selectedTasks.size }))), _jsx(WeekPlannerElementComponent, { element: item, isDragging: snapshot.isDragging, openTaskDetailModal: task => openTaskDetailModal(task), onTasksUpdated: mergeUpdatedTasks, onUpdateExecutionDate: onUpdateExecutionDate, showDeleteTask: true, onDeleteTask: setTaskToDelete, selectionMode: canUpdateTask, isSelected: isSelected, onToggleSelected: (element, event) => onSelectionClick(element, event, column.date.format('YYYY-MM-DD'), column.items) })] })));
    };
    const renderColumnItems = (column, dropProvided, dropSnapshot) => {
        const overbooked = column.availableMinutes < 0;
        const selectableDayItems = column.items.filter(isSelectable);
        return (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ className: "week-planner-header" }, { children: [_jsxs("div", Object.assign({ className: "week-planner-header-day" }, { children: [_jsx("span", { children: canUpdateTask && (_jsx(Checkbox, { value: selectableDayItems.length > 0 &&
                                            selectableDayItems.every(i => selectedTasks.has(i.id)), onClick: newValue => setManySelected(selectableDayItems, newValue) })) }), _jsx("span", { children: intl.formatMessage(weekdayMessages[column.date.isoWeekday() - 1]) }), _jsx("span", {})] })), _jsx("div", Object.assign({ className: "week-planner-header-sub" }, { children: dateFormatService.formatDate(column.date) })), column.isHoliday && column.totalTime === 0 ? (_jsx("div", Object.assign({ className: "week-planner-header-capacity" }, { children: intl.formatMessage(weekPlannerMessages.Holiday) }))) : (_jsxs("div", Object.assign({ className: "week-planner-header-capacity" }, { children: [column.isHoliday ? (_jsx("div", { children: intl.formatMessage(weekPlannerMessages.Holiday) })) : (_jsxs("div", { children: [intl.formatMessage(weekPlannerMessages.WorkingTime), ":", ' ', minutesToTimeSpan(column.workingMinutes)] })), _jsxs("div", { children: [intl.formatMessage(weekPlannerMessages.Planned), ":", ' ', minutesToTimeSpan(column.totalTime)] }), _jsxs("div", Object.assign({ className: overbooked ? 'week-planner-header-overbooked' : '' }, { children: [intl.formatMessage(weekPlannerMessages.Available), ":", ' ', minutesToTimeSpan(column.availableMinutes)] }))] })))] })), _jsxs("div", Object.assign({ ref: dropProvided.innerRef }, dropProvided.droppableProps, { className: "week-planner-column-body" }, { children: [(column.registrations || []).map((reg, idx) => {
                            const resourceName = reg.resourceId
                                ? intl.formatMessage(listItemMsg('resource', reg.resourceId, reg.resourceName))
                                : reg.resourceName;
                            return (_jsxs("div", Object.assign({ className: "week-planner-registration-block" }, { children: [_jsx("span", Object.assign({ className: `week-planner-registration-type week-planner-registration-type-${reg.type}` }, { children: getResourceTypeLabel(intl, reg.type) })), _jsx("span", Object.assign({ className: "week-planner-registration-name", title: resourceName }, { children: resourceName })), _jsx("span", Object.assign({ className: "week-planner-registration-duration" }, { children: minutesToTimeSpan(reg.durationMinutes) }))] }), `reg-${idx}`));
                        }), (column.externalEvents || []).map(event => {
                            // Every status except Busy gets a status-specific colour + translated label prefix.
                            const isBusy = event.showAs === CalendarEventShowAs.Busy;
                            return (_jsxs("div", Object.assign({ className: `week-planner-calendar-block${isBusy ? '' : ` week-planner-calendar-status-${event.showAs}`}` }, { children: [_jsx("i", { className: "fa fa-calendar week-planner-calendar-icon", "aria-hidden": "true" }), _jsxs("span", Object.assign({ className: "week-planner-calendar-title", title: event.subject }, { children: [!isBusy && (_jsxs("span", Object.assign({ className: "week-planner-calendar-status-label" }, { children: [intl.formatMessage(listItemMsg('calendarEventShowAs', event.showAs)), ":", ' '] }))), event.subject] })), _jsx("span", Object.assign({ className: "week-planner-calendar-time" }, { children: formatExternalEventWhen(event) }))] }), `calendar-${event.id}`));
                        }), _jsx("div", { children: dropProvided.placeholder }), column.items.map((item, index) => (_jsx(Draggable, Object.assign({ draggableId: item.id, index: index, isDragDisabled: !canUpdateTask }, { children: (provided, snapshot) => renderItem(provided, snapshot, item, column) }), item.id)))] }))] }));
    };
    const renderColumn = column => (_jsx("div", Object.assign({ className: "week-planner-column" }, { children: _jsx(Droppable, Object.assign({ droppableId: column.id, type: "ITEM", isDropDisabled: !canUpdateTask }, { children: (dropProvided, dropSnapshot) => renderColumnItems(column, dropProvided, dropSnapshot) })) }), column.id));
    return (_jsxs(_Fragment, { children: [_jsx(StableDragDropContext, Object.assign({ onDragStart: onDragStart, onDragEnd: onDragEnd }, { children: _jsxs("div", Object.assign({ className: "week-planner week-planner-layout", ref: setWeekPlannerLayoutRef }, { children: [_jsxs("div", Object.assign({ className: "week-planner-main" }, { children: [_jsx(WeekPlannerHeader, { activeFilters: activeFilters, setFilter: setFilter, defaultFilters: getEmptyState(), onCreateToDo: () => setShowCreateToDoModal(true), onTogglePlanTasks: () => setUnplannedTasksOpen(prev => !prev), isPlanTasksOpen: isUnplannedTasksOpen, showWeekend: showWeekend, onToggleWeekend: setShowWeekend, weekendTaskCount: weekendTaskCount, selectAllChecked: allSelected, onToggleSelectAll: canUpdateTask ? toggleSelectAll : undefined, selectionActions: canUpdateTask ? (_jsx(WeekPlannerSelectionActions, { onSetExecutionDate: () => setShowSetDateModal(true), onMoveToNextWeek: moveSelectionToNextWeek, onMoveToNextFreeMoment: openPlanProposal, onChangeAssignedUser: () => setShowChangeAssignedUserModal(true), disabled: selectedTasks.size === 0 || isBulkUpdating })) : (undefined) }), _jsx(LoadingContainer, Object.assign({ isLoading: isLoading && !data }, { children: _jsx("div", Object.assign({ className: "week-planner-container" }, { children: columns.map(column => renderColumn(column)) })) }))] })), _jsx(WeekPlannerUnplannedTasksPanel, { isOpen: isUnplannedTasksOpen, onClose: () => setUnplannedTasksOpen(false), filters: effectiveUnplannedTasksFilters, onFiltersChanged: setUnplannedTasksFilters, showFilters: showUnplannedTasksFilters, onToggleFilters: () => setShowUnplannedTasksFilters(prev => !prev), items: unplannedTaskItems, isLoading: isUnplannedTasksLoading, isFetchingNextPage: isUnplannedTasksFetchingNextPage, hasNextPage: hasNextUnplannedTasksPage, onLoadMore: () => fetchNextUnplannedTasksPage(), openTaskDetailModal: openTaskDetailModal, onTasksUpdated: mergeUpdatedTasks, onUpdateExecutionDate: onUpdateExecutionDate, selectionMode: canUpdateTask, selectedTasks: selectedTasks, onToggleSelected: (element, event) => onSelectionClick(element, event, 'unplanned', unplannedTaskItems), onDeselectAll: clearSelection, multiDragActive: multiDragActive })] })) })), showCreateToDoModal && (_jsx(CreateToDoModal, { show: true, onSave: onToDoCreated, onClose: () => setShowCreateToDoModal(false) })), taskDetailModal.open && (_jsx(TaskDetailsModal, { show: true, task: { id: (_b = taskDetailModal.task) === null || _b === void 0 ? void 0 : _b.id }, onSave: closeTaskDetailModal, onClose: closeTaskDetailModal })), taskToDelete && (_jsx(YesNoModal, { show: true, title: intl.formatMessage(taskMessages.DeleteTodo), message: intl.formatMessage(taskMessages.DeleteTodoConfirmationMsg), onNo: () => setTaskToDelete(null), onSave: () => deleteTodo(taskToDelete) })), showSetDateModal && (_jsx(WeekPlannerExecutionDateModal, { show: true, saving: isBulkUpdating, selectedDeadlines: selectedTaskDeadlines, onSave: submitBulkExecutionDate, onClose: () => setShowSetDateModal(false) })), showChangeAssignedUserModal && (_jsx(ChangeAssignedUserModal, { show: true, intl: intl, onSave: submitBulkAssignedUser, onClose: () => setShowChangeAssignedUserModal(false) })), showProposalModal && (_jsx(WeekPlannerPlanProposalModal, { show: true, loading: isProposalLoading, rows: proposalRows, saving: isBulkUpdating, onSave: savePlanProposal, onClose: () => setShowProposalModal(false) }))] }));
};
export default showWhenPermission(functionalities.PlannedTask, privileges.Read)(injectStateService(WeekPlannerContainer));
